/**
 * Draft view-mode, filter, validate, and lifecycle action toolbar.
 */

import type {
  ProtocolLifecycleAction,
  ProtocolViewMode,
} from "./protocolDetailTypes";

type ProtocolDraftToolbarProps = {
  busy: boolean;
  viewMode: ProtocolViewMode;
  filterText: string;
  onViewModeChange: (mode: ProtocolViewMode) => void;
  onFilterTextChange: (value: string) => void;
  onValidate: () => void;
  onLifecycle: (action: ProtocolLifecycleAction) => void;
};

/**
 * Renders draft workspace controls above the Flow/Table/DAG views.
 *
 * @param props - Toolbar props
 * @returns Toolbar element
 */
export function ProtocolDraftToolbar({
  busy,
  viewMode,
  filterText,
  onViewModeChange,
  onFilterTextChange,
  onValidate,
  onLifecycle,
}: ProtocolDraftToolbarProps) {
  return (
    <section className="flex flex-wrap items-center gap-2">
      {(
        [
          ["flow", "Flow"],
          ["live", "Live T1"],
          ["table", "Table"],
          ["dag", "DAG"],
        ] as const
      ).map(([mode, label]) => (
        <button
          key={mode}
          className={`rounded px-3 py-1.5 text-sm ${
            viewMode === mode
              ? "bg-slate-900 text-white"
              : "border border-slate-300"
          }`}
          onClick={() => onViewModeChange(mode)}
        >
          {label}
        </button>
      ))}
      <input
        className="rounded border border-slate-300 px-3 py-1.5 text-sm"
        placeholder="Filter criteria/atoms"
        value={filterText}
        onChange={(event) => onFilterTextChange(event.target.value)}
      />
      <button
        className="rounded border border-slate-300 px-3 py-1.5 text-sm"
        disabled={busy}
        onClick={() => void onValidate()}
      >
        Validate
      </button>
      <button
        className="rounded border border-slate-300 px-3 py-1.5 text-sm"
        disabled={busy}
        onClick={() => void onLifecycle("request-review")}
      >
        Request review
      </button>
      <button
        className="rounded border border-slate-300 px-3 py-1.5 text-sm"
        disabled={busy}
        onClick={() => void onLifecycle("request-changes")}
      >
        Request changes
      </button>
      <button
        className="rounded border border-slate-300 px-3 py-1.5 text-sm"
        disabled={busy}
        onClick={() => void onLifecycle("approve")}
      >
        Approve
      </button>
      <button
        className="rounded bg-emerald-700 px-3 py-1.5 text-sm text-white"
        disabled={busy}
        onClick={() => void onLifecycle("publish")}
      >
        Publish
      </button>
      <button
        className="rounded border border-red-300 px-3 py-1.5 text-sm text-red-700"
        disabled={busy}
        onClick={() => void onLifecycle("discard")}
      >
        Discard
      </button>
    </section>
  );
}
